import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import SharedForm from "../../components/ui/Form";
import FileProgress from "../../components/ui/FileProgress";
import withAuth from "../../components/hoc/withAuth";
import messages from "../../utils/helper/messages";
import { isObjectEmpty, isValidName, radioOptionValues } from '../../utils/utils';
import { ALERT_TYPE, FILE_TYPE, MIME_TYPES, POSITIONS, ALIGN, FILE_TYPE_VALUE, COHORT_FILE_STATUS, MEMBER_FILE_TYPE } from '../../constants';
import { AlertContext } from "../../context/AlertContext";
import Loader from "../../components/ui/Loader";
import api from '../../services/api/memberAnalyticsService';

const UpdateCohortModal = ({ data, show, onCancel, isReUpload }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [cohortDetails, setCohortDetails] = useState(null);
    const [selectedFileType, setSelectedFileType] = useState('');
    const [file, setFile] = useState("");
    const [progress, setProgress] = useState(0);
    const progressRef = useRef(null);

    const { showAlert } = useContext(AlertContext);
    const currentUser = useSelector((state) => state.user.currentUser);
    const allowedFileTypes = [MIME_TYPES.CSV];

    useEffect(() => {
        if (cohortDetails?.documentUpload) {
            setSelectedFileType(cohortDetails.documentUpload);
        }
    }, [cohortDetails?.documentUpload]);

    useEffect(() => {
        if (data) {
            if (isReUpload) {
                setCohortDetails({ cohortName: data.cohortName, name: data.name });
            } else {
                setCohortDetails({ name: data.name });
            }
        }
    }, [data]);

    const arr = useMemo(() => [
        {
            key: isReUpload ? 'cohortName' : 'name',
            labelName: 'Cohort Name',
            placeholderName: 'Cohort Name',
            type: 'text',
            disabled: true,
            required: false
        },
        {
            key: isReUpload ? 'name' : 'cohortUpdateName',
            labelName: 'Update Name',
            placeholderName: 'Update Name',
            type: 'text',
            maxLength: 32
        },
        {
            type: 'radio',
            key: 'documentUpload',
            labelName: 'Document Upload',
            optionValues: radioOptionValues(FILE_TYPE_VALUE, false, true),
            maxLength: 12,
            resetFields: ['file', 'systemFile'],
        },
        {
            key: 'file',
            type: 'file',
            allowedTypes: allowedFileTypes,
            fileNameKey: 'fileName',
            hidden: selectedFileType !== FILE_TYPE.Local,
            labelName: 'Local File',
            required: selectedFileType === FILE_TYPE.Local,
        },
        {
            key: 'systemFile',
            type: 'systemFile',
            allowedTypes: allowedFileTypes,
            fileNameKey: 'systemFileName',
            hidden: selectedFileType !== FILE_TYPE.System,
            labelName: 'System File',
            required: selectedFileType === FILE_TYPE.System,
            fileApi: () => api.GetCohortUpdateFiles(MEMBER_FILE_TYPE.COHORT_UPDATE)
        }
    ], [selectedFileType]);

    const handleSuccess = (fileObj) => {
        showAlert(`${fileObj.originalFileName} ${messages.success.cohortUpload}`, ALERT_TYPE.SUCCESS);
        setIsLoading(false);
        setProgress(0);
        onHideCallback();
    };

    const handleError = (error) => {
        setIsLoading(false);
        setProgress(0);
        showAlert(`${messages.error.prefix} ${error}.`, ALERT_TYPE.ERROR);
    };

    const handleSubmit = async (formData) => {
        setIsLoading(true);
        const file = cohortDetails.file;
        const isLocalFile = selectedFileType === FILE_TYPE.Local;

        let fileObj = new File(
            [file],
            `${currentUser.companyId}_${formData.cohortUpdateName}_${isLocalFile ? file.name : cohortDetails.fileName}`,
            { type: isLocalFile ? file.type : MIME_TYPES.CSV }
        );

        Object.assign(fileObj, {
            companyId: currentUser.companyId,
            uploaderUserId: currentUser.id,
            cohortUpdateName: formData.cohortUpdateName,
            originalFileName: cohortDetails.fileName,
            status: COHORT_FILE_STATUS.UPLOADED,
            cohortId: data.id,
            isLocalFile
        });

        if (isReUpload) {
            fileObj.cohortUpdateId = data.id;
            fileObj.cohortUpdateName = formData.name;
            fileObj.cohortId = data.cohortId;
        }

        const uploadFn = isReUpload ? api.editCohortUpdateUpload : api.UpdateCohortUpdateInput;

        try {
            await uploadFn(fileObj, (event) => {
                progressRef.current = Math.round((100 * event.loaded) / event.total);
                setProgress(progressRef.current);
                if (progressRef.current === 100) {
                    setCohortDetails(null);
                    setProgress(0);
                }
            }).then(() => handleSuccess(fileObj)).catch(handleError);
        } catch (error) {
            handleError(error);
        }
    };

    const handleCohortDetailsChange = (update) => {
        setCohortDetails((details) => ({ ...details, ...update }));
    };

    const onHideCallback = () => {
        setCohortDetails(null);
        setFile("");
        onCancel();
    };

    const handleInputChange = (name, value) => {
        if (name === "documentUpload") {
            setSelectedFileType(value);
            handleCohortDetailsChange({ documentUpload: value, fileName: '', file: null, systemFile: null });
        } else if (name === "file") {
            handleCohortDetailsChange({ fileName: value.name, file: value});
            setFile(value);
        } else if (name === "systemFile") {
            handleCohortDetailsChange({ fileName: value.name, systemFile: value });
            setFile(value);
        } else {
            handleCohortDetailsChange({ [name]: value });
        }
    };

    const isValidCohortInfo = () => !isObjectEmpty(cohortDetails) && isValidName(cohortDetails.cohortUpdateName);

    return (
        <Modal show={show} backdrop="static" onHide={onHideCallback} size="md">
            <Modal.Header closeButton>
                <Modal.Title>Update Cohort</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-3 py-xl-2 position-relative">
                    {data === null && <Loader />}
                    <SharedForm
                        cols={12}
                        array={arr}
                        modelObj={cohortDetails}
                        onSubmit={handleSubmit}
                        onCancel={onHideCallback}
                        onInputChanged={handleInputChange}
                        loading={isLoading}
                        disabled={!isValidCohortInfo}
                        actionBarPosition={POSITIONS.TOP}
                        actionBarAlign={ALIGN.RIGHT}
                    />
                    {progress > 0 && <FileProgress file={file} progress={progress} wrapperStyle="mt-2" />}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default withAuth(UpdateCohortModal);
